import { createAsyncThunk } from "@reduxjs/toolkit";
import apiConfig from "constants/apiConfig";
import { getCollabChatRoom } from "services/api/chat";
import { fetchCollaborationMilestone, fetchCollaborationTasks, fetchCollaborationTasksSummary } from "services/api/collaboration";
import { AuthState } from "slices/auth/reducer";
import { ERROR } from "types";
import { CollaborationChatRoom } from "types/chat";
import { CollaborationItem, CollaborationsSlice, CollaborationTasksSummary, MilestoneData } from "types/collaboration";
import { generateUniqueKey, getErrorMessage, isFresh } from "utils/helpers";

export const fetchAllCollaborationsThunk = createAsyncThunk<{count: number, items: CollaborationItem[], gig_types: any[], gig_steps: any[], filters: { page: number, [key: string]: any }, filterId: string}, {filters: { page: number, [key: string]: any }, force?: boolean}, {rejectValue: string}>(
    'collaborations',
    async({filters, force}, {getState, rejectWithValue}) => {
        try {
            const state = getState() as { auth: AuthState, collaborations: CollaborationsSlice };
            const filterId = generateUniqueKey(filters, ['page']);
            const STALE_TIME = apiConfig.FETCH_COLLABORATION_TASKS.staleTime;

            if(filters.page === 1 && !force && isFresh(state.collaborations?.filtered_lists?.[filterId]?.last_fetched_at, STALE_TIME)){
                return rejectWithValue(ERROR.CACHED);
            }

            const hubId = state.auth.selectedHubId;
            if(!hubId) throw new Error("No hub selected!");

            const {count, items, gig_types, gig_steps} = await fetchCollaborationTasks(hubId, {...filters, limit: 24});

            return {count, items, filters, filterId, gig_types, gig_steps}

        } catch (error) {
            return rejectWithValue(getErrorMessage(error, "Failed to fetch collaboration!"));
        }
    }
)

export const fetchCollaborationMilestoneData = createAsyncThunk<{data: MilestoneData, collaborationId: string, campaignId: string, milestoneId: string, cache?: boolean}, {campaignId: string, milestoneId: string, collaborationId: string, force?: boolean}, {rejectValue: string}>(
    'campaign/collaboration/milestone',
    async ({campaignId, collaborationId, milestoneId, force}, { getState, rejectWithValue }) => {
        try { 
            const STALE_TIME = apiConfig.FETCH_COLLABORATION_MILESTONE.staleTime;
            const state = getState() as {auth: AuthState, collaborations: CollaborationsSlice};
            const existingData = state.collaborations.milestones?.[campaignId]?.[collaborationId]?.[milestoneId] ?? null
            if(!force && isFresh(existingData?.last_fetched_at ?? null, STALE_TIME)){
                return {milestoneId, campaignId, collaborationId, data: existingData.milestone, cache: true}
            }
            const hubId = state.auth.selectedHubId ?? '';
            const data = await fetchCollaborationMilestone(hubId, campaignId, collaborationId, milestoneId);
            return {milestoneId, campaignId, collaborationId, data};
        } catch (error) {
            return rejectWithValue('Failed to fetch collaboration milestone');
        }
    }
)

export const fetchCollaborationTasksSummaryThunk = createAsyncThunk<{ summary: CollaborationTasksSummary }, { force?: boolean }, { rejectValue: string }>(
    'collaboration/task/summary',
    async ({ force }, { getState, rejectWithValue }) => {
        try {
            const state = getState() as { auth: AuthState, collaborations: CollaborationsSlice };
            const STALE_TIME = apiConfig.FETCH_COLLABS_SUMMARY.staleTime;
            const selectedHubId = state.auth.selectedHubId;

            if(!selectedHubId) throw new Error("Hub not selected!");

            if (!force && isFresh(state.collaborations.summary.last_fetched_at, STALE_TIME)) {
                return rejectWithValue(ERROR.CACHED);
            }

            const summary = await fetchCollaborationTasksSummary(selectedHubId);

            return { summary }

        } catch (error) {
            return rejectWithValue(getErrorMessage(error, "Failed to fetch collaboration task summary!"));
        }
    }
)


export const fetchCollaborationChatRoom = createAsyncThunk<{room: CollaborationChatRoom, collaborationId: string, campaignId: string, cache?: boolean}, {campaignId: string, collaborationId: string, force?: boolean}, {rejectValue: string}>(
    'campaign/collaboration/chatroom',
    async ({campaignId, collaborationId, force}, { getState, rejectWithValue }) => {
        try { 
            const STALE_TIME = apiConfig.FETCH_COLLABORATION_MILESTONE.staleTime;
            const state = getState() as {auth: AuthState, collaborations: CollaborationsSlice};
            const existingData = state.collaborations.chatRooms[campaignId]?.[collaborationId] ?? null
            if(!force && isFresh(existingData?.last_fetched_at ?? null, STALE_TIME)){
                return {campaignId, collaborationId, room: existingData.room, cache: true}
            }
            const hubId = state.auth.selectedHubId ?? '';
            const room = await getCollabChatRoom(hubId, campaignId, collaborationId);
            return {campaignId, collaborationId, room, cache: false};
        } catch (error) {
            return rejectWithValue('Failed to fetch collaboration chatroom');
        }
    }
)
